import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Label,
  Spinner,
  PasswordInput,
} from "@opero/ui";
import { SsoButtons, type SsoButtonsProvider } from "@opero/ui/auth";
import { OPERO_LOGOS } from "@opero/ui/brand";
import { getErrorMessage, PolarisApiError } from "@digiits-agency/polaris-sdk";
import {
  buildTokenRedirectUrl,
  tokenStorage,
  type LoginResponse,
} from "@opero/shared";
import { polaris, APP_URL } from "../lib/polaris-client";
import { passwordSchema } from "../lib/password";
import { startSso } from "../lib/sso";
import { AuthModalShell } from "./AuthModalShell";

/**
 * New account: the password is being *chosen*, so hold it to the product's
 * strength rules.
 */
const newAccountSchema = z.object({
  firstName: z.string().trim().optional(),
  lastName: z.string().trim().optional(),
  password: passwordSchema,
});

/**
 * Existing account: the password is being *proved*, not created. Validating it
 * against the current strength rules would lock out anyone whose password
 * predates them, and the real check is the server's — a wrong one comes back
 * as a 401 we re-prompt on.
 */
const existingAccountSchema = newAccountSchema.extend({
  password: z.string().min(1, "Enter your password"),
});

type AcceptFormValues = z.infer<typeof newAccountSchema>;

type View = "loading" | "form" | "invalid" | "success";

type SsoEnforcement = "optional" | "required" | "disabled";

interface InvitationInfo {
  email: string;
  tenantName: string;
  ssoEnforcement: SsoEnforcement;
  ssoProviders: SsoButtonsProvider[];
  /**
   * Whether this email already has an account in this product.
   *
   * Deliberately three-state. The backend omits the field when it cannot tell,
   * and `undefined` must not collapse into `false`: promising "choose a
   * password" to someone who already has one produces a 401 they have no way
   * to interpret. Unknown gets neutral copy and the lenient schema.
   */
  hasAccount?: boolean;
}

interface AcceptInviteModalProps {
  isOpen: boolean;
  token: string;
  onClose: () => void;
  onSwitchToLogin: () => void;
}

export function AcceptInviteModal({
  isOpen,
  token: tokenProp,
  onClose,
  onSwitchToLogin,
}: AcceptInviteModalProps) {
  const [searchParams] = useSearchParams();
  const token = tokenProp || searchParams.get("token") || "";
  const redirectTo = searchParams.get("redirect") || APP_URL;

  const [view, setView] = useState<View>("loading");
  const [apiError, setApiError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [invitation, setInvitation] = useState<InvitationInfo | null>(null);

  // `useForm` captures its options on first render, but which schema applies
  // isn't known until validateToken resolves — so keep the resolver stable and
  // let it pick the schema at validation time.
  const hasAccountRef = useRef<boolean | undefined>(undefined);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<AcceptFormValues>({
    resolver: (values, context, options) =>
      zodResolver(
        hasAccountRef.current === true
          ? existingAccountSchema
          : newAccountSchema,
      )(values, context, options),
    defaultValues: { firstName: "", lastName: "", password: "" },
  });

  useEffect(() => {
    if (!isOpen) return;
    setApiError(null);
    setInvitation(null);
    reset();

    if (!token) {
      setView("invalid");
      setApiError("No invitation token provided.");
      return;
    }

    setView("loading");
    let cancelled = false;
    polaris.users
      .validateToken({ token })
      .then((r) => {
        if (cancelled) return;
        if (!r.valid) {
          setView("invalid");
          setApiError("This invitation link is invalid or has expired.");
          return;
        }
        hasAccountRef.current = r.hasAccount;
        setInvitation({
          email: r.email ?? "",
          tenantName: r.tenantName ?? "",
          ssoEnforcement: r.ssoEnforcement ?? "disabled",
          hasAccount: r.hasAccount,
          ssoProviders: (r.ssoProviders ?? []).map((p) => ({
            connectionId: p.connectionId,
            providerType: p.providerType,
            displayName: p.displayName,
            iconUrl: p.logoUrl,
          })),
        });
        reset({
          firstName: r.firstName ?? "",
          lastName: r.lastName ?? "",
          password: "",
        });
        setView("form");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setView("invalid");
        setApiError(getErrorMessage(err, "Unable to validate invitation"));
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, token, reset]);

  const onSubmit = async (data: AcceptFormValues) => {
    if (!token || !invitation) return;
    setApiError(null);
    setIsLoading(true);
    try {
      // Drop any existing session in opero-web so accept goes out unauthenticated
      // and we don't keep the inviter's tokens locally after the redirect.
      tokenStorage.clearTokens();

      const res = await polaris.users.accept({
        token,
        body: {
          password: data.password,
          firstName: data.firstName?.trim() || undefined,
          lastName: data.lastName?.trim() || undefined,
        },
      });

      let accessToken = res.accessToken ?? "";
      let refreshToken = res.refreshToken ?? "";
      if (!accessToken) {
        const login = (await polaris.auth.login({
          body: { email: invitation.email, password: data.password } as unknown as Parameters<
            typeof polaris.auth.login
          >[0]["body"],
        })) as LoginResponse;
        accessToken = login.accessToken ?? "";
        refreshToken = login.refreshToken ?? "";
      }

      setView("success");
      window.location.href = buildTokenRedirectUrl(
        redirectTo,
        accessToken,
        refreshToken,
      );
    } catch (err: unknown) {
      // Accept fails in ways the user can act on, and a single generic banner
      // hides which one happened. 401 in particular is recoverable in place:
      // it means the account already existed and the password was wrong.
      const status =
        err instanceof PolarisApiError ? err.statusCode : undefined;
      const message = getErrorMessage(err, "Failed to accept invitation");

      if (status === 401) {
        // Only reachable on the existing-account branch — and proof of it, so
        // correct the copy if the backend left `hasAccount` unset.
        hasAccountRef.current = true;
        setInvitation((prev) => (prev ? { ...prev, hasAccount: true } : prev));
        setError("password", {
          message: "That password doesn't match this account.",
        });
      } else if (status === 409) {
        setApiError(
          /sso/i.test(message)
            ? "This organization requires SSO. Use one of the sign-in options above."
            : `${message} Sign in to continue.`,
        );
      } else {
        setApiError(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSsoSelect = async (provider: SsoButtonsProvider) => {
    if (!token) return;
    setApiError(null);
    setIsLoading(true);
    try {
      tokenStorage.clearTokens();
      await startSso(provider, { mode: "login", inviteToken: token });
    } catch (err: unknown) {
      setApiError(getErrorMessage(err, "Failed to start SSO sign-in"));
      setIsLoading(false);
    }
  };

  const goToLogin = () => {
    onClose();
    onSwitchToLogin();
  };

  return (
    <AuthModalShell
      isOpen={isOpen}
      onClose={onClose}
      brand={<img src={OPERO_LOGOS.logoFull} alt="Opero" className="h-7" />}
    >
      <div className="px-7 pb-7 pt-2">
        {view === "loading" && (
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <Spinner size="lg" />
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Validating your invitation…
            </p>
          </div>
        )}

        {view === "invalid" && (
          <div className="space-y-4 py-4 text-center">
            <Alert variant="destructive">
              <AlertDescription className="text-sm text-danger">
                {apiError ??
                  "This invitation link is invalid or has expired."}
              </AlertDescription>
            </Alert>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Ask your admin to resend the invitation, or sign in if you
              already have an account.
            </p>
            <Button variant="outline" className="w-full" onClick={goToLogin}>
              Back to sign in
            </Button>
          </div>
        )}

        {view === "success" && (
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <Spinner size="lg" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Welcome to Opero
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Signing you in…
            </p>
          </div>
        )}

        {view === "form" && invitation && (() => {
          const ssoRequired = invitation.ssoEnforcement === "required";
          const showSso =
            invitation.ssoEnforcement !== "disabled" &&
            invitation.ssoProviders.length > 0;
          const showPasswordForm = !ssoRequired;
          // The password field means two different things here. For an
          // identity that already exists, accepting *joins* this
          // organization onto it and the field proves who they are; for a
          // new one it creates the credential. Never promise either when
          // `hasAccount` is absent — see `InvitationInfo.hasAccount`.
          const headerCopy = ssoRequired
            ? "Sign in with your organization's SSO provider to accept."
            : invitation.hasAccount === true
              ? "Confirm your password to join."
              : invitation.hasAccount === false
                ? "Set a password to accept."
                : "Enter your password to accept.";
          const passwordLabel =
            invitation.hasAccount === true
              ? "Password"
              : invitation.hasAccount === false
                ? "Create a password"
                : "Your password";
          const passwordPlaceholder =
            invitation.hasAccount === true
              ? "Your existing password"
              : invitation.hasAccount === false
                ? "Create a password"
                : "Enter your password";
          // An existing identity already has a name on it; asking again
          // implies the answer will be applied, and it won't be.
          const showNameFields = invitation.hasAccount !== true;
          return (
            <>
              <div className="mb-5">
                {/* `tenantName` is optional and the backend does not always
                  * populate it; "Join " with a blank after it reads as a
                  * rendering bug, so drop to a name-free heading. */}
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                  {invitation.tenantName
                    ? `Join ${invitation.tenantName}`
                    : "Accept your invitation"}
                </h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  You&apos;ve been invited as{" "}
                  <strong>{invitation.email}</strong>. {headerCopy}
                </p>
              </div>

              {apiError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription className="text-sm text-danger">
                    {apiError}
                  </AlertDescription>
                </Alert>
              )}

              {showSso && (
                <div className={showPasswordForm ? "mb-4" : ""}>
                  <SsoButtons
                    providers={invitation.ssoProviders}
                    onSelect={handleSsoSelect}
                    isLoading={isLoading}
                    separatorLabel={
                      showPasswordForm
                        ? invitation.hasAccount === true
                          ? "Or use your password"
                          : "Or set a password"
                        : ""
                    }
                  />
                </div>
              )}

              {showPasswordForm && (
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  noValidate
                  className="space-y-4"
                >
                  {showNameFields && (
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label>First name</Label>
                        <Input
                          {...register("firstName")}
                          placeholder="Optional"
                        />
                        {errors.firstName && (
                          <p className="text-xs text-red-500">
                            {errors.firstName.message}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1.5">
                        <Label>Last name</Label>
                        <Input
                          {...register("lastName")}
                          placeholder="Optional"
                        />
                        {errors.lastName && (
                          <p className="text-xs text-red-500">
                            {errors.lastName.message}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <Label>{passwordLabel}</Label>
                    <PasswordInput
                      {...register("password")}
                      autoComplete={
                        invitation.hasAccount === true
                          ? "current-password"
                          : "new-password"
                      }
                      placeholder={passwordPlaceholder}
                    />
                    {errors.password && (
                      <p className="text-xs text-red-500">
                        {errors.password.message}
                      </p>
                    )}
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Spinner size="sm" /> Accepting…
                      </>
                    ) : (
                      "Accept invitation"
                    )}
                  </Button>
                </form>
              )}

              {/* "Already have an account?" is the wrong question once we
                * know they do — for them the footer's job is an escape
                * hatch, not a correction. */}
              <p className="mt-4 text-center text-sm text-slate-500 dark:text-slate-400">
                {invitation.hasAccount === true
                  ? "Prefer to do this later? "
                  : "Already have an account? "}
                <button
                  type="button"
                  onClick={goToLogin}
                  className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
                >
                  Sign in
                </button>
              </p>
            </>
          );
        })()}
      </div>
    </AuthModalShell>
  );
}
